import type { Column, FilterFn, Table } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown, Columns3, Filter, X } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** Обязателен на КАЖДОЙ фильтруемой колонке. Мы фильтруем сами (`manualFiltering`), но без явного
 *  `filterFn` библиотека подбирает его по типу первого значения и для чисел ставит `inNumberRange`,
 *  чей `autoRemove` ждёт кортеж `[min, max]`. Наш объект `{min, max}` он считает пустым и УДАЛЯЕТ
 *  фильтр ровно в тот момент, когда его выставили, — фильтрация по числам не работала совсем. */
export function keepFilter<T>(): FilterFn<T> {
  return () => true;
}

/** Тип фильтра колонки. `number` — диапазон от/до, `text` — подстрока. */
/** `list` — как `text`, но несколько значений через пробел, запятую, «;» или перевод строки: «любое из»
 *  (для артикулов: в них нет пробелов, а столбец из Excel вставляется в поле через пробелы). */
export type ColFilterKind = "number" | "text" | "list";

export interface NumberFilter {
  min?: number;
  max?: number;
}

/** Мета колонки: подпись, вид фильтра, выравнивание. Живёт в `columnDef.meta`. */
export interface ColMeta {
  title: string;
  filter?: ColFilterKind;
  left?: boolean; // текстовая колонка (иначе число: правое выравнивание + tabular-nums)
}

export const metaOf = (c: { columnDef: { meta?: unknown } }): ColMeta =>
  (c.columnDef.meta ?? { title: "" }) as ColMeta;

/** Проходит ли значение фильтр колонки. Одна реализация на все таблицы раздела.
 *  «Пусто» разбирает `filterActive` — здесь его не дублируем, иначе правило «снятый фильтр ≡
 *  отсутствующий» жило бы в двух местах и могло разъехаться. */
export function passesFilter(kind: ColFilterKind, value: unknown, filter: unknown): boolean {
  if (!filterActive(kind, filter)) return true;
  if (kind === "number") {
    const { min, max } = filter as NumberFilter;
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return false;
    return (min == null || n >= min) && (max == null || n <= max);
  }
  const hay = String(value ?? "").toLowerCase();
  if (kind === "list") {
    return listTokens(filter).some((t) => hay.includes(t));
  }
  return hay.includes(String(filter).trim().toLowerCase());
}

/** Значения фильтра-списка: через пробел, запятую, «;» или перевод строки, без пустых. */
function listTokens(filter: unknown): string[] {
  return String(filter ?? "")
    .toLowerCase()
    .split(/[\s,;]+/)
    .filter(Boolean);
}

/** Есть ли в значении фильтра хоть что-то (пустой фильтр считается снятым). */
export function filterActive(kind: ColFilterKind, filter: unknown): boolean {
  if (filter == null) return false;
  if (kind === "number") {
    const { min, max } = filter as NumberFilter;
    return min != null || max != null;
  }
  return String(filter).trim() !== "";
}

/** Человекочитаемое описание фильтра — для чипа над таблицей. */
export function filterLabel(kind: ColFilterKind, filter: unknown): string {
  if (kind === "number") {
    const { min, max } = filter as NumberFilter;
    if (min != null && max != null) return `${min}…${max}`;
    return min != null ? `≥ ${min}` : `≤ ${max}`;
  }
  if (kind === "list") {
    const t = listTokens(filter);
    return t.length > 1 ? `любой из ${t.length}: ${t.slice(0, 3).join(", ")}${t.length > 3 ? "…" : ""}` : `«${t[0]}»`;
  }
  return `«${String(filter)}»`;
}

/** Выделенные строки, которые ВИДНЫ при текущих фильтрах/поиске, — ровно то, с чем работает
 *  любое массовое действие над выделением. Скрытые фильтром галочки не теряются: они остаются в
 *  `selected` и вернутся в счёт, как только фильтр снимут, — но в действие не попадают. Иначе
 *  кнопка над отфильтрованной таблицей показывает «14 позиций» там, где человек видит 3, и
 *  отправляет заодно 11 чужих. Порядок результата — порядок видимых строк.
 *  Побочно отсекает «мёртвые» ключи: выделение переживает перезагрузку (localStorage), и товара
 *  с таким ключом в текущих данных может уже не быть. */
export function visibleSelection<T>(
  selected: ReadonlySet<string>,
  visible: readonly T[],
  keyOf: (row: T) => string,
): Set<string> {
  const picked = new Set<string>();
  for (const row of visible) {
    const key = keyOf(row);
    if (selected.has(key)) picked.add(key);
  }
  return picked;
}

function NumberEditor({ value, onChange }: {
  value: NumberFilter;
  onChange: (v: NumberFilter | undefined) => void;
}): React.ReactElement {
  const num = (s: string): number | undefined => (s.trim() === "" ? undefined : Number(s));
  const set = (patch: NumberFilter): void => {
    const next = { ...value, ...patch };
    onChange(next.min == null && next.max == null ? undefined : next);
  };
  return (
    <div className="flex items-center gap-1.5">
      <Input
        type="number"
        placeholder="от"
        className="h-8"
        value={value.min ?? ""}
        onChange={(e) => set({ min: num(e.target.value) })}
      />
      <span className="text-muted-foreground">—</span>
      <Input
        type="number"
        placeholder="до"
        className="h-8"
        value={value.max ?? ""}
        onChange={(e) => set({ max: num(e.target.value) })}
      />
    </div>
  );
}

/** Заголовок колонки: подпись (клик — сортировка), кнопка фильтра, ручка ресайза. */
export function HeaderCell<T>({ column, onResize, last }: {
  column: Column<T, unknown>;
  onResize: (e: React.MouseEvent) => void;
  last: boolean;
}): React.ReactElement {
  const meta = metaOf(column);
  const sorted = column.getIsSorted();
  const kind = meta.filter;
  const raw = column.getFilterValue();
  const active = kind ? filterActive(kind, raw) : false;
  // один жест — один обработчик: сортируют и подпись, и иконка, и раньше у них разошлись
  // и гард `getCanSort()`, и подсказка
  const sortProps = column.getCanSort()
    ? {
        onClick: (e: React.MouseEvent) => column.toggleSorting(undefined, e.shiftKey),
        title: "Сортировать (Shift — добавить к текущей)",
      }
    : {};
  return (
    // overflow-hidden обязателен: у грид-элемента автоминимум = min-content, и длинные подписи
    // («Будущая глубина») распирали бы таблицу горизонтально, ломая fr-раскладку
    <div className="relative flex min-w-0 flex-col justify-between overflow-hidden px-2 py-1">
      {/* Подпись — на своей строке и с переносом: рядом с иконками сортировки и фильтра она
          обрезалась до «Заказ…»/«Будуща…», и колонку нельзя было опознать. */}
      <button
        type="button"
        {...sortProps}
        className={cn(
          "text-left text-[11px] leading-tight hover:text-foreground",
          !meta.left && "text-right",
          sorted && "text-foreground",
        )}
      >
        {meta.title}
      </button>
      {/* Управление — второй строкой, поэтому не отъедает ширину у названия */}
      <div className={cn("flex items-center gap-1", !meta.left && "justify-end")}>
        {column.getCanSort() && (
          <button
            type="button"
            {...sortProps}
            className={cn("rounded p-0.5 hover:bg-accent", sorted ? "text-primary" : "opacity-30 hover:opacity-100")}
          >
            {sorted === "asc" ? (
              <ArrowUp className="size-3" />
            ) : sorted === "desc" ? (
              <ArrowDown className="size-3" />
            ) : (
              <ChevronsUpDown className="size-3" />
            )}
          </button>
        )}
        {kind && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "shrink-0 rounded p-0.5 hover:bg-accent",
                  active ? "text-primary" : "opacity-30 hover:opacity-100",
                )}
                title={active ? "Фильтр включён" : "Фильтр по колонке"}
              >
                <Filter className="size-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-2">
              <div className="mb-1.5 text-xs font-medium">{meta.title}</div>
              {kind === "number" && (
                <NumberEditor
                  value={(raw as NumberFilter) ?? {}}
                  onChange={(v) => column.setFilterValue(v)}
                />
              )}
              {(kind === "text" || kind === "list") && (
                <Input
                  className="h-8"
                  placeholder={kind === "list" ? "один или несколько — через пробел/запятую" : "содержит…"}
                  value={(raw as string) ?? ""}
                  onChange={(e) => column.setFilterValue(e.target.value || undefined)}
                />
              )}
              {active && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-1.5 h-7 w-full"
                  onClick={() => column.setFilterValue(undefined)}
                >
                  <X className="size-3.5" /> Снять фильтр
                </Button>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>
      {!last && (
        <div
          onMouseDown={onResize}
          className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40"
        />
      )}
    </div>
  );
}

/** Полоса активных фильтров. Без неё спрятанный в шапке фильтр — ловушка: человек забывает
 *  про него и решает, что данные пропали. */
export function FilterChips<T>({ table, extra }: {
  table: Table<T>;
  extra?: { key: string; label: string; onClear: () => void }[];
}): React.ReactElement | null {
  // колонки и внешние фильтры приводим к ОДНОЙ форме — иначе одна и та же кнопка-чип
  // существовала бы в двух копиях и уже начала бы расходиться по стилю
  const chips = [
    ...table.getAllLeafColumns().flatMap((c) => {
      const kind = metaOf(c).filter;
      const v = c.getFilterValue();
      return kind && filterActive(kind, v)
        ? [{
            key: c.id,
            label: `${metaOf(c).title} ${filterLabel(kind, v)}`,
            onClear: () => c.setFilterValue(undefined),
          }]
        : [];
    }),
    ...(extra ?? []),
  ];
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-md border bg-card px-3 py-1.5 text-xs">
      <span className="text-muted-foreground">Фильтры:</span>
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={c.onClear}
          className="flex items-center gap-1 rounded border bg-background px-1.5 py-0.5 hover:bg-accent"
          title="Снять фильтр"
        >
          {c.label}
          <X className="size-3" />
        </button>
      ))}
      <button
        type="button"
        onClick={() => {
          table.resetColumnFilters();
          extra?.forEach((e) => e.onClear());
        }}
        className="ml-1 text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      >
        снять все
      </button>
    </div>
  );
}

/** Меню видимости колонок — прямой ответ на «13 колонок, часть видна только со скроллом». */
export function ColumnsMenu<T>({ table }: { table: Table<T> }): React.ReactElement {
  const cols = table.getAllLeafColumns().filter((c) => c.getCanHide());
  const hidden = cols.filter((c) => !c.getIsVisible()).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" title="Какие колонки показывать">
          <Columns3 className="size-4" />
          Колонки
          {hidden > 0 && <span className="ml-1 text-xs text-muted-foreground">−{hidden}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <div className="grid gap-0.5">
          {cols.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={c.getIsVisible()}
                onChange={c.getToggleVisibilityHandler()}
              />
              {metaOf(c).title}
            </label>
          ))}
        </div>
        {hidden > 0 && (
          <Button
            size="sm"
            variant="ghost"
            className="mt-1.5 h-7 w-full"
            onClick={() => table.resetColumnVisibility()}
          >
            Показать все
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
